/**
 * Common functions for UI components.
 */

import UberCommon from 'saymon-uber-common';
import gettext from 'utils/gettext';
import tinycolor from 'tinycolor';
import _ from 'underscore';
import $ from 'jquery';
import 'jquery.purl';
import 'smartNotification';
import clientConfig from './client-config-ex';

let $window = $(window);

let cssjs = require('exports-loader?CssJs=window.cssjs!css-js');
let serverTimeDelta = 0; // Deference between server and client time.

// Setup AJAX to allow empty JSON response.
$.ajaxSetup({
  dataFilter: function(data, type) {
    if (type == 'json' && data === '') {
      data = null;
    }

    return data;
  }
});

/**
 * Generates an entity HTML link.
 *
 * @param {String|Number} entityType Entity type.
 * @param {String|Number} entityId Entity ID.
 * @param {String} entityName Entity name.
 * @returns {String} Entity link HTML or empty string, if an entity link cannot be generated.
 */
let entityLink = function(entityType, entityId, entityName) {
  let entityStringType;

  if (entityType == UberCommon.numericEntityTypes.Object) {
    entityStringType = 'objects';
  }
  else if (entityType == UberCommon.numericEntityTypes.Link) {
    entityStringType = 'links';
  }

  return entityStringType && '<a href="/index.html#/' + entityStringType + '/' + entityId +
    '" data-objid="' + entityId + '" target="_blank">' + _.escape(entityName) + '</a>' || '';
};

/**
 * Converts HTTP status code to user-friendly message.
 *
 * @param {Number} status HTTP status code.
 * @param {String} dflt Default message.
 * @returns {String} User message string.
 */
let httpStatusToText = function(status, dflt) {
  switch (status) {
    case 0:
      return gettext('Network is unreachable. Please ensure you have an Internet access and try again.');

    case 400:
      return gettext('Invalid input data');

    case 403:
      return gettext('Insufficient rights for operation');

    case 404:
      return gettext('Object not found');

    case 405:
      return gettext('Method not implemented');

    case 409:
      return gettext('Invalid input data');

    case 500:
      return gettext('Internal server error');

    case 502:
      return gettext('Server not responding');

    case 504:
      return gettext('Timeout');

    default:
      return dflt;
  }
};

/**
 * Converts SAYMON status code to user-friendly message.
 *
 * @param {Number} status SAYMON status code.
 * @param {String} dflt Default message.
 * @param {Object|undefined} payload Status message payload.
 * @returns {String} User message string.
 */
let saymonStatusToText = function(status, dflt, payload) {
  let sc = UberCommon.saymonStatusCodes;

  switch (status) {
    case sc.DocumentConversionPending:
      return gettext('Document is awaiting for conversion and is not available yet');

    case sc.BadFileType:
      return gettext('Invalid file format');

    case sc.BadLogin:
      return gettext('Invalid login or password');

    case sc.BadCurrentPassword:
      return gettext('Invalid password');

    case sc.ProcessingTimeout:
      return gettext('The process was terminated by timeout');

    case sc.UserAlreadyExists:
      return gettext('User already exists');

    case sc.PropertyAlreadyExists:
      return gettext('Property "%s" already exists').replace('%s', payload.propertyName);

    case sc.TemplateSyntaxError:
      return gettext('Syntax error in template');

    case sc.LicenseViolationError:
      return gettext('License limit exceeded');

    case sc.LinkedObjectRemoval:
      return gettext('Cannot remove an object, because it is being used in the system');

    case sc.BadCaptcha:
      return gettext('You entered an invalid code value from the image');

    case sc.MailSenderError:
      return gettext('Error sending mail, please try again later');

    case sc.EmailDoesNotExists:
      return gettext('Email %s not found').replace('%s', payload.email);

    case sc.EmailAlreadyUsed:
      return gettext('Email %s has already occupied').replace('%s', payload.email);

    case sc.ServiceUnavailable:
      return gettext('%s service unavailable').replace('%s', payload.serviceName);

    case sc.OperationIsUsedInTriggersError:
      return gettext('Operation is used in triggers');

    case sc.OperationIsUsedInTriggersOfDependentObjectsError:
      return gettext('The operation is used in triggers of dependent objects') + ': ' + payload.objects.join(',\n');

    default:
      return dflt;
  }
};

let debug = $.url().param('debug') || false;

// Attempt to split debug setting by categories.
if (debug) {
  if (debug != 'true') {
    debug = debug.split(',');
  }
  else {
    debug = true;
  }
}

let exports = {
  entityLink: entityLink,

  /**
   * HTML non-breaking space symbol.
   */
  NON_BREAKING_SPACE: '\u00A0',

  /**
   * Checks if debug is enabled for a given category.
   *
   * @param {String|String[]} [category] Debug category name. If not specified, the function checks
   * if debug is at all enabled. If array is specified, the function checks if debug is enabled for
   * any of listed categories.
   * @returns {Boolean} True if debug is enabled, false otherwise.
   */
  isDebug: function(category) {
    if (!debug) return false; // Not enabled.

    if (!category) return true; // Enabled for at least some category.

    if (debug === true) return true; // Enabled for all categories.

    if (!_.isArray(category)) return _.contains(debug, category);

    return _.some(category, c => _.contains(debug, c));
  },

  /**
   * Prints a debug message if a given debug category is enabled.
   *
   * @param {String} category Debug category.
   * @param {*} msg Debug message or message parts.
   */
  debug: function(category, ...msg) {
    if (exports.isDebug(category)) {
      console.log(...msg);
    }
  },

  /**
   * Prints a debug message with timestamp to console.
   *
   * @param {String} msg Message to write. Additional arguments may follow that are inserted
   * to console.log() as is.
   */
  debugMessage: function(msg) {
    let date = new Date();
    let msg0 = `[${exports.localizedDateTime(date)}.${date.getMilliseconds()}] ${msg}`;
    let args = _.toArray(arguments);
    args[0] = msg0;

    console.log.apply(console, args);
  },

  /**
   * Displays error alert in a specified container.
   *
   * @param {String|jQuery} containerSel Container selector or jQuery object. If string is specified -
   * a selector is implied, and a container element is looked up from this object
   * using this, find(), and closest().
   * @param {String} msg Message to display.
   * @param {String} [headline] Message headline.
   * @returns {Object} Error alert jQuery object.
   * @deprecated Use errorBox instead.
   */
  displayErrorAlert: function(containerSel, msg, headline) {
    let $this = $(this);
    let $container = _.isObject(containerSel) ? containerSel : $this.find(containerSel);

    if ($container.length === 0) {
      $container = $this.closest(containerSel);
    }

    if ($container.length === 0) {
      $container = $(containerSel);
    }

    let $alert = $container.find('.alert.alert-danger');

    if ($alert.length > 0) {
      $alert.find('.msg').text(msg).end().fadeIn();
    }
    else {
      $alert = $('<div class="alert alert-danger">' +
        '<button type="button" class="close" data-dismiss="alert">&nbsp;&times;</button>' +
        '<strong>' + (headline || gettext('Error') + '!') + '</strong> <span class="msg">' + msg + '</span>' +
        '</div>');

      let $floatPanel = $container.find('.floating-panel');

      if ($floatPanel.length === 0) {
        $container.prepend($alert);
      }
      else {
        // For correct positioning, when floating-panel sections are shown.
        $('.js-active-section').before($alert);

        $container.find('.static-block').css('margin-top', $alert.height() * 1.6);

        $alert.on('remove', function() {
          $container.find('.static-block').css('margin-top', '');
          $floatPanel.removeAttr('style');
        });
      }
    }

    return $alert;
  },

  /**
   * Displays agent error alert in a specified container.
   *
   * @param {String|Object} containerSel Container selector (see displayErrorAlert() for details).
   * @param {Object} agentError Agent error object.
   * @returns {Object} Error alert jQuery object.
   */
  displayAgentErrorAlert: function(containerSel, agentError) {
    let msg = agentError.message;

    if (agentError.errorCode) {
      msg = saymonStatusToText(agentError.errorCode, gettext('Error') + ' ' + agentError.errorCode) +
        ' (' + msg + ')';
    }

    return exports.displayErrorAlert.call(this, containerSel, msg, gettext('Sensor error') + '!');
  },

  /**
   * Converts an XHR with server error to Saymon error message.
   *
   * @param {Object} jqXHR AJAX response.
   * @param {String} [errorThrown] An errorThrown parameter from AJAX response.
   * @returns {String} Error message string.
   */
  xhrToSaymonError: function(jqXHR, errorThrown) {
    let msg = (jqXHR.status > 0 ? jqXHR.status + ' ' : '') + httpStatusToText(jqXHR.status, errorThrown);

    // Check for Saymon status code.
    if (jqXHR.responseText) {
      try {
        let respObj = JSON.parse(jqXHR.responseText);

        if (respObj.message) {
          let respMsgObj = JSON.parse(respObj.message);

          if (respMsgObj.errorCode) {
            msg = '(' + respMsgObj.errorCode + ') ' +
              saymonStatusToText(respMsgObj.errorCode, respMsgObj.message, respMsgObj.payload);
          }
        }
      }
      catch (err) {
      }
    }

    return msg;
  },

  /**
   * Displays an error message box.
   *
   * @param {String|Error|XMLHttpRequest} msg Error message, object, or response.
   * @param {Object} [options] Operation options.
   * - {Boolean} [mute] Error box mute sound flag.
   * - {String} [prefix] Message prefix.
   * @returns {jQuery} Notification box element.
   */
  errorBox: function(msg, options) {
    options = options || {};

    let prefix = options.prefix || '';
    let errText;

    if (_.isString(msg)) {
      errText = msg;
    }
    else if (msg instanceof Error) {
      errText = msg.message;
    }
    else {
      errText = exports.xhrToSaymonError(msg);
    }

    $.smallBox({
      title: gettext('Error') + '!',
      content: prefix + _.escape(errText),
      color: '#ff0000',
      timeout: 10000,
      icon: 'fa fa-exclamation-circle',
      sound: !options.mute
    });

    // eslint-disable-next-line no-undef
    return $('#smallbox' + SmallBoxes);
  },

  /**
   * Displays a warning message box.
   *
   * @param {String} msg Warning message.
   * @param {Object} [options] Operation options.
   * - {Boolean} [mute] Warning box mute sound flag.
   * @returns {jQuery} Notification box element.
   */
  warningBox: function(msg, options) {
    options = options || {};

    $.smallBox({
      title: gettext('Warning'),
      content: _.escape(msg),
      color: '#dfb56c',
      icon: 'fa fa-exclamation-triangle',
      sound: !options.mute
    });

    // eslint-disable-next-line no-undef
    return $('#smallbox' + SmallBoxes);
  },

  /**
   * Displays an OK message box.
   *
   * @param {String} msg OK message.
   * @param {Object} [options] Operation options.
   * - {Boolean} [mute] Warning box mute sound flag.
   * @returns {jQuery} Notification box element.
   */
  okBox: function(msg, options) {
    options = options || {};

    $.smallBox({
      title: gettext('Notification') + '!',
      content: _.escape(msg),
      color: '#8ac38b',
      timeout: 5000,
      icon: 'fa fa-check',
      sound: !options.mute
    });

    // eslint-disable-next-line no-undef
    return $('#smallbox' + SmallBoxes);
  },

  /**
   * Removes message boxes, created by errorBox(), warningBox(), or okBox().
   */
  clearMessageBoxes: function() {
    $('#divSmallBoxes').empty();
  },

  /**
   * Checks if a given XHR was aborted.
   *
   * @param {Object} jqHXR AJAX request.
   * @returns {Boolean} True if aborted, false otherwise.
   */
  xhrIsAborted: function(jqHXR) {
    return jqHXR.status === 0 && jqHXR.statusText == 'abort';
  },

  /**
   * Show error error validation tooltip.
   *
   * @param {String|jQuery} $el Validated input.
   * @param {String} errorText Tooltip text.
   * @param {String|jQuery} [container] Appends the tooltip to a specific element.
   */
  setErrorTooltip: function($el, errorText, container) {
    let tooltipParams = {
      title: errorText,
      trigger: 'hover',
      template: '<div class="tooltip invalid-tooltip" role="tooltip"><div class="tooltip-arrow"></div>' +
      '<div class="tooltip-inner"></div></div>',
      placement: 'auto'
    };

    if (container) {
      tooltipParams.container = container;
    }

    $($el).tooltip(tooltipParams).tooltip('show');
  },

  /**
   * Generates AJAX loader image HTML.
   *
   * @returns {String} Image HTML.
   */
  getAjaxLoader: function() {
    return '<img src="/images/ajax-loader-small.gif" alt="">';
  },

  /**
   * Reads an option field, first asserting it is defined.
   *
   * @param {Object} o Option object.
   * @param {String} name Option name.
   * @returns {*} Option value.
   */
  requireOption: function(o, name) {
    if (!o) {
      throw new Error('Option object is undefined.');
    }

    if (!o[name]) {
      throw new Error('Undefined option: ' + name);
    }

    return o[name];
  },

  /**
   * Checks an object for presence of required option fields. Throws Error
   * if the check does not pass.
   *
   * @param {Object} o Option object to check.
   * @param {String[]} fields Array of required option field names.
   */
  requireOptions: function(o, fields) {
    if (!o) {
      throw new Error('Option object is undefined.');
    }

    _.each(fields, function(field) {
      if (!_.has(o, field)) {
        throw new Error('Missing required option: ' + field);
      }
    });
  },

  /**
   * AJAX error handling function. Displays an error message alert as a first
   * element in a given container.
   *
   * @param {String|jQuery} containerSel Container selector, which is looked up from this object
   * using this, find(), and closest().
   * @param {Object} jqXHR AJAX request.
   * @param {String} [textStatus] Text status from AJAX request.
   * @param {Object} [errorThrown] Error from AJAX request.
   * @returns {Object} Error alert jQuery element.
   */
  displayAjaxError: function(containerSel, jqXHR, textStatus, errorThrown) {
    return exports.errorBox.call(this, exports.xhrToSaymonError(jqXHR, errorThrown));
  },

  /**
   * Caches a result of an original function in this object
   * after first call and uses the cached result for all
   * subsequent calls.
   *
   * @param {String} key Cache key.
   * @param {Function} f Original function.
   * @param {Number} [timeout] Timeout for cache invalidation.
   * @returns {*} Original function result.
   */
  cached: function(key, f, timeout) {
    let cache = this._cache;

    if (!cache) {
      cache = this._cache = {};
    }

    let expired = false;

    if (timeout > 0) {
      let cacheTs = this._cacheTs;
      let now = _.now();

      if (!cacheTs) {
        cacheTs = this._cacheTs = {};
      }

      let ts = cacheTs[key];

      if (!ts || (now - ts >= timeout)) { // Cache key expired.
        expired = true;
        cacheTs[key] = now;
      }
    }

    let ret = expired ? undefined : cache[key];

    if (!ret) {
      ret = cache[key] = f.apply(this, arguments);
    }

    return ret;
  },

  /**
   * Caches a result of an original function in container object
   * after first call and uses the cached result for all
   * subsequent calls.
   *
   * @param {Object} container Cache container object.
   * @param {Function} f Original function.
   * @param {Number} [timeout] Timeout for cache invalidation.
   * @returns {*} Original function result.
   */
  cached0: function(container, f, timeout) {
    return exports.cached.call(container, f.name, f, timeout);
  },

  /**
   * Invalidates cache under a given key (or keys), set by cached() function.
   * The keys are specified as varargs.
   *
   * @param {Object} obj Object with cache.
   */
  invalidateCache: function(obj) {
    let cache = obj && obj._cache;

    if (!cache) return;

    let keys = _.toArray(arguments);

    keys.shift(); // Remove first argument.

    if (!_.isEmpty(keys)) {
      _.each(keys, function(key) {
        delete cache[key];
      });
    }
    else {
      delete obj._cache;
    }
  },

  /**
   * Returns a decimal separator, that is used in current locale.
   *
   * @return {String} Decimal separator.
   */
  decimalSeparator: _.constant((1.1).toLocaleString().substring(1, 2)),

  /**
   * Generates a localized time unit string for a given duration in genitive case.
   *
   * @param {Array[]} duration Duration 2-element array: first element in time value,
   * second element is time unit string. This array is normally a result of bringToUnit()
   * function.
   * @returns {String} Time unit string.
   */
  timeUnitGenitive: function(duration) {
    let singularUnit = duration[1].substr(0, duration[1].length - 1);
    let pluralUnit = duration[1];

    return gettext.ngettext(singularUnit + '_genitive', pluralUnit + '_genitive', duration[0]);
  },

  /**
   * Tests if a given number is a fraction.
   *
   * @param {Number} value Number to test.
   * @returns {Boolean} True if given number is a fraction, false otherwise.
   */
  isFraction: function(value) {
    return Boolean(value - parseInt(value, 10));
  },

  /**
   * Tests if a given string is a hyperlink.
   *
   * @param {String} value Value to test.
   * @returns {Boolean} True if given value is a hyperlink, false otherwise.
   */
  isHyperlink: function(value) {
    return value && value.match(/^\w+:\/\/.*/) !== null;
  },

  /**
   * Parses a valid floating point number string into float value.
   *
   * @param {String} value Floating point string.
   * @returns {Number} Floating point number, or undefined, if the string
   * does not represent a floating point number.
   */
  parseFloat: function(value) {
    if (isFinite(value)) {
      let ret = parseFloat(value);

      if (ret + 1) {
        return ret;
      }
    }
  },

  /**
   * Removes and returns a first predicate-matching value from the input array.
   *
   * @param {Array} arr Input array.
   * @param {Function} predicate Predicate.
   * @returns {*} Value that was matched and removed, or undefined, if no value was found.
   */
  pull: function(arr, predicate) {
    let idx = -1;

    _.some(arr, function(elem, idx0) {
      if (predicate(elem)) {
        idx = idx0;

        return true;
      }
    });

    if (idx >= 0) {
      let ret = arr[idx];

      arr.splice(idx, 1);

      return ret;
    }
  },

  /**
   * Clears and sets timeout on a given method of this object.
   *
   * @param {String} methodName Name of the method on which to set timeout.
   * @param {Number} period Time period for setTimeout().
   */
  resetTimeout: function(methodName, period) {
    let timeouts = this._timeouts;

    if (!timeouts) {
      timeouts = this._timeouts = {};
    }

    clearTimeout(timeouts[methodName]);
    timeouts[methodName] = setTimeout(_.bind(this[methodName], this), period);
  },

  /**
   * Clears a timeout, that was set with resetTimeout() function.
   *
   * @param {String} methodName Name of the method on which to set timeout.
   */
  clearTimeout: function(methodName) {
    this._timeouts && clearTimeout(this._timeouts[methodName]);
  },

  /**
   * Measures repeatedly the number of times this method is called per second.
   *
   * @param {String} counterName Name of the counter to use.
   * @param {Function} tickCallback Callback that is invoked every second with value
   * of times per second as argument.
   */
  measureTimesPerSecond: function(counterName, tickCallback) {
    let counters = this._counters;

    if (!counters) {
      counters = this._counters = {};
    }

    if (_.isUndefined(counters[counterName])) {
      counters[counterName] = 1;

      setInterval(function() {
        tickCallback(counters[counterName]);

        counters[counterName] = 0;
      }, 1000);
    }
    else {
      counters[counterName]++;
    }
  },

  /**
   * Displays a given AJAX loader after a specified delay. Provides cancelling capability.
   *
   * @param {Object} loader Loader to display with show() and hide() methods implemented
   * (typically, a jQuery object).
   * @param {Number} delay Display delay in milliseconds.
   * @returns {Object} Loader handle with cancel() method, which cancels the timer and hides
   * the loader.
   */
  delayLoader: function(loader, delay) {
    let timeout = setTimeout(function() {
      loader.show();
    }, delay);

    return {
      cancel: function() {
        clearTimeout(timeout);

        loader.hide();
      }
    };
  },

  /**
   * Sequentially runs synchronous and asynchronous actions. Actions are functions,
   * that should return deferred objects or promises in case they are asynchronous.
   *
   * @returns {Object} Sequence completion promise.
   */
  sequential: function() {
    let args = _.toArray(arguments);
    let dfd = new $.Deferred();

    let nextHandler = function() {
      let arg = args.shift();

      if (!arg) return dfd.resolve();

      let ret = arg();
      let curDfd;

      if (_.isArray(ret)) {
        curDfd = $.when.apply($, ret);
      }
      else if (exports.isPromise(ret)) {
        curDfd = ret;
      }
      else {
        return nextHandler(); // Assume the function was synchronous.
      }

      curDfd.done(nextHandler).fail(_.bind(dfd.reject, dfd));
    };

    nextHandler();

    return dfd.promise();
  },

  /**
   * Replaces hyphen-separated string to camel case string.
   * Example: foo-bar-bazz => fooBarBazz.
   *
   * @param {String} str Input string.
   * @returns {String} Result string.
   */
  hyphenToCamel: function(str) {
    return str.replace(/-([a-z])/g, function(g) {
      return g[1].toUpperCase();
    });
  },

  /**
   * Replaces camel case string to hyphen-separated string.
   * Example: fooBarBazz => foo-bar-bazz.
   *
   * @param {String} str Input string.
   * @returns {String} Result string.
   */
  camelToHyphen: function(str) {
    return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  },

  /**
   * Serializes jQuery form object into a JavaScript data object.
   *
   * @param {jQuery} $form Form element.
   * @returns {Object} Plain JavaScript object with form data.
   */
  formToObject: function($form) {
    let ret = _.reduce($form.serializeArray(), function(o, elem) {
      if (o[elem.name] !== undefined) {
        if (!o[elem.name].push) {
          o[elem.name] = [o[elem.name]];
        }

        o[elem.name].push(elem.value || '');
      }
      else {
        o[elem.name] = elem.value || '';
      }

      return o;
    }, {});

    // Extend with data from all named components.
    $form.find('[data-component][name]').each(function() {
      let $this = $(this);

      ret[$this.attr('name')] = $this.val();
    });

    return ret;
  },

  /**
   * Tests if an element is visible in scroll container, fully or partially.
   *
   * @param {jQuery} $elem Element to test.
   * @param {jQuery} [$viewport] Element viewport (scrollable block). Window by default.
   * @returns {Boolean} True if visible fully or partially, false otherwise.
   */
  isVisibleInScroll: function($elem, $viewport = $window) {
    if (!$elem.is(':visible')) return false;

    let viewport = {};

    viewport.top = $viewport.scrollTop();
    viewport.bottom = viewport.top + $viewport.height();

    let bounds = {};

    bounds.top = $elem.offset().top;
    bounds.bottom = bounds.top + $elem.outerHeight();

    return ((bounds.top <= viewport.bottom) && (bounds.bottom >= viewport.top));
  },

  /**
   * Filter if an elements is visible in scroll container.
   *
   * @param {jQuery} $elems Elements jQuery selector.
   * @param {jQuery} [$viewport] Element viewport (scrollable block). Window by default.
   * @returns {jQuery} Filtered visible elements.
   */
  isVisibleInScrollFilter: function($elems, $viewport = $window) {
    $elems = $elems.filter(':visible');

    if (!$elems.length) return [];

    let viewport = {};

    viewport.top = $viewport.scrollTop();
    viewport.bottom = viewport.top + $viewport.height();

    $elems.filter((i, elem) => {
      let $elem = $(elem);
      let bounds = {};

      bounds.top = $elem.offset().top;
      bounds.bottom = bounds.top + $elem.outerHeight();

      return ((bounds.top <= viewport.bottom) && (bounds.bottom >= viewport.top));
    });

    return $elems;
  },

  /**
   * Checks if a vertical scrollbar on a given element is scrolled to the end.
   *
   * @param {jQuery} $elem Container element to check.
   * @returns {Boolean} True if element is scrolled to the end, false otherwise.
   */
  isScrolledToTheEnd: function($elem) {
    return $elem[0].scrollHeight - $elem.outerHeight() - $elem.scrollTop() <= $elem[0].scrollHeight * 0.2;
  },

  /**
   * Checks whether an input is a Promise.
   *
   * @param {*} input Input.
   * @returns {Boolean} True if input is a Promise, false otherwise.
   */
  isPromise: function(input) {
    return _.isObject(input) && _.isFunction(input.done) && _.isFunction(input.fail);
  },

  /**
   * Computes the CSS property value from the specified CSS class.
   *
   * @param {String} cssClass CSS class name.
   * @param {String|String[]} propName CSS property name or array of property names.
   * @returns {String|String[]} Property value for the specified property name, or empty string, if the
   * value is undefined. May also return array of string values, if the input was an array of names (the
   * indexes correspond).
   */
  getCssProperty: function(cssClass, propName) {
    // We need a fake CSS element to extract actual CSS properties from a class.
    let $el = $('#get-css-property');
    $el = $el.length ? $el : $('<div>').attr('id', 'get-css-property').css({ display: 'none' }).appendTo('body');
    $el.removeClass().addClass(cssClass);

    if (_.isArray(propName)) {
      return _.map(propName, function(propName0) {
        return $el.css(propName0);
      });
    }

    return $el.css(propName);
  },

  /**
   * Computes the CSS 'content' property value for the specified class. Appends ':before'
   * selector to the specified class.
   *
   * @param {String} cssClass CSS class name.
   * @returns {String} Content property value, or empty string, if the value is not defined.
   */
  getCssContent: function(cssClass) {
    // We need a fake CSS element to extract actual CSS properties from a class.
    let $el = $('#get-css-content');
    $el = $el.length ? $el : $('<div>').attr('id', 'get-css-content').appendTo('body');
    $el.removeClass().addClass(cssClass);

    let content = window.getComputedStyle(document.querySelector('.' + cssClass), ':before')
      .getPropertyValue('content');

    return content == 'none' ? '' : content;
  },

  /**
   * Checks whether development mode is enabled.
   *
   * @returns {Boolean} True if development mode is enabled, false otherwise.
   */
  devMode: function() {
    return window.devMode;
  },

  /**
   * Calculates the byte size of a string.
   *
   * @param {String} value Input string.
   * @returns {Number} String size in bytes.
   */
  byteSize: function(value) {
    if (_.isObject(value)) {
      let objectList = [];
      let stack = [value];
      let bytes = 0;
      let iterateObjectFields = function(value0, key0) {
        stack.push(value0);
        stack.push(key0);
      };

      while (stack.length) {
        let stackItem = stack.pop();

        if (typeof stackItem === 'object' && objectList.indexOf(stackItem) == -1) {
          objectList.push(stackItem);

          _.each(stackItem, iterateObjectFields);
        }
        else {
          bytes += exports.byteSize(stackItem);
        }
      }

      return bytes;
    }
    else if (typeof value === 'boolean') {
      return 4;
    }
    else if (typeof value === 'string') {
      return value.length * 2;
    }
    else if (typeof value === 'number') {
      return 8;
    }
  },

  /**
   * Parses numeric RGB color from CSS string.
   *
   * @param {String} s RGB string in CSS format (#xyz or rgb(x,y,z)).
   * @returns {Number[]} Numeric array with red (index 0), green (index 1), and blue (index 2) color values.
   */
  parseRgb: function(s) {
    if (s.charAt(0) == '#') { // Format: #xyz.
      return [
        parseInt(s.charAt(1) + s.charAt(2), 16),
        parseInt(s.charAt(3) + s.charAt(4), 16),
        parseInt(s.charAt(5) + s.charAt(6), 16)
      ];
    }
    else { // Format: rgb(x,y,z).
      let res = s.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);

      return [
        parseInt(res[1], 10),
        parseInt(res[2], 10),
        parseInt(res[3], 10)
      ];
    }
  },

  /**
   * Copies text to clipboard.
   * User action is needed for normal functioning, for example click.
   *
   * @param {String} text Text to copy.
   * @returns {Boolean} If text was copied return true.
   */
  copyToClipboard: function(text) {
    let result;
    let $textarea = $('<textarea>').css({
      position: 'absolute',
      top: -2000,
      left: -2000
    }).val(text).appendTo('body');

    // If we have element with tabindex=-1 in body tag copy will not work.
    let $tabIndexNeg = $('body > *[tabindex="-1"]');
    $tabIndexNeg.removeAttr('tabindex');
    $textarea.select();
    result = document.execCommand('copy');
    $tabIndexNeg.attr('tabindex', -1);

    $textarea.remove();

    return !!result;
  },

  /**
   * Returns a 2-lowercase-letter browser language, like "ru" or "en";
   * if browser language is defined in extended form (like "en-GB") -
   * brings it to 2-letter form.
   *
   * @returns {String} Two-letter language code.
   */
  normalizedLanguage: function() {
    let lang = localStorage.getItem('locale') || navigator.language;
    lang = lang == 'root' ? 'en' : lang;
    let extMatch = lang.match(/([a-z]{2})-.+/); // Try to match for extended representation (like, en-GB).

    if (extMatch) {
      return extMatch[1];
    }

    return lang;
  },

  /**
   * Sets locale to local storage and reload page.
   *
   * @param {String} lang User locale. If equal to 'root' convert to 'en'.
   */
  setLocale(lang) {
    localStorage.setItem('locale', lang);
    location.reload();
  },

  /**
   * Gets system locales from client config.
   * Check for 'root' locale was added for compatibility with old releases.
   *
   * @param {Object} clientConfig Client Config for prevent cycling require.
   * @returns {Array} Array of items with locale, short and long name.
   */
  getLanguages(clientConfig) {
    return Object.keys(clientConfig.languages).map((langKey) => {
      return {
        locale: langKey == 'root' ? 'en' : langKey,
        ...clientConfig.languages[langKey]
      };
    });
  },

  /**
   * Declares abstract method.
   *
   * @param {String} methodName Method name.
   */
  abstractMethod: function(methodName) {
    throw new Error('Method "' + methodName + '" is abstract and should be implemented in subclasses.');
  },

  /**
   * Custom implementation of sort in scroll container in dragtable plugin.
   *
   * @param {Object} originalTable Our real table. Not used. Need for compatibility with beforeMoving callback.
   * @param {Object} sortableTable Created by dragtable plugin visualization of sortable table.
   */
  scrollSortableTable: function(originalTable, sortableTable) {
    sortableTable.el.on('sort', function(event, ui) {
      // Scroll container must be marked by class .js-dragtable-scroll
      let $parentScroll = sortableTable.el.parents('.js-dragtable-scroll').eq(0);

      if (!$parentScroll.length) return;

      let scrollLeft = $parentScroll.scrollLeft();
      // Move table to left on scroll value.
      $(sortableTable.el).css('left', -scrollLeft);

      let helperWidth = ui.helper.width();
      let containerLeft = $parentScroll.offset().left;
      let scrollSensitivity = $(this).sortable('option', 'scrollSensitivity');
      let scrollSpeed = $(this).sortable('option', 'scrollSpeed');

      // Set position of draggable element.
      ui.helper.css('left', event.pageX - containerLeft + scrollLeft - helperWidth / 2);
      let helperLeft = ui.helper.offset().left;

      // Moves table left or right when draggable element is out $parentScroll.
      if (helperLeft - containerLeft < scrollSensitivity) {
        $parentScroll.scrollLeft(scrollLeft - scrollSpeed);
      }
      else if (containerLeft + $parentScroll.width() - helperLeft - helperWidth < scrollSensitivity) {
        $parentScroll.scrollLeft(scrollLeft + scrollSpeed);
      }
    });
  },

  /**
   * Drag helper for objects and link waypoints.
   *
   * @param {Object} options Helper options:
   * -{Object} dragStepParams Katavorio drag step params:
   *  -{Object} drag The associated Drag instance;
   *  -{Event} e The associated MouseEvent;
   *  -{DOM} el The element that is being dragged;
   *  -{Array} pos [x,y] location of the element. Only supplied by the drag event.
   * -{Object} linkEngine jsPlumb instance.
   * -{Number|undefined} gridDim grid dimension or no grid snap.
   */
  dragHelper: function(options) {
    let params = options.dragStepParams;

    /**
     * Prevent object zero positions.
     *
     * @param {Array} pos Left and top object position.
     */
    let repaint = function(pos) {
      let pos0 = {
        left: pos[0] <= 0 ? 0 : pos[0],
        top: pos[1] <= 0 ? 0 : pos[1]
      };
      options.linkEngine.repaint(params.el, pos0);
      params.drag.params.setPosition(params.el, [pos0.left, pos0.top]);
    };

    // Snap draggable element to grid.
    if (options.gridDim) {
      let remainder = {
        x: params.pos[0] % options.gridDim,
        y: params.pos[1] % options.gridDim
      };

      repaint([
        params.pos[0] - remainder.x,
        params.pos[1] - remainder.y
      ]);
    }
    else if (params.pos[0] <= 0 || params.pos[1] <= 0) {
      repaint(params.pos);
    }
  },

  /**
   * Incrementally triggers events, given a colon-separated event name or array of names.
   *
   * Example: for 'change:mode:preview' or ['change', 'mode', 'preview']
   * event, a 'change', 'change:mode', and 'change:mode:preview'
   * will be triggered.
   *
   * Arguments, following documented parameters, are passed to trigger()
   * call.
   *
   * @param {Object} sender Object, which is triggering changes.
   * @param {String|Array} eventName Event name, possibly colon-separated or array of event names.
   */
  triggerIncremental: function(sender, eventName) {
    let eventNamesArray = _.isArray(eventName) ? eventName : eventName.split(':');
    let args0 = _.toArray(_.without(arguments, sender, eventName));

    _.reduce(eventNamesArray, function(memo, elem) {
      sender.trigger.apply(sender, [memo + elem].concat(args0));

      return memo + elem + ':';
    }, '');
  },

  /**
   * Enables event loop lag monitoring. Useful for detecting UI performance bottlenecks.
   *
   * @param {Function} cb Callback, which is called periodically with current event loop
   * lag value.
   */
  monitorEventLoopLag: function(cb) {
    let lastLagCheckTime = _.now();
    let lagCheckInterval = 1000;

    setInterval(() => {
      let now = _.now();
      let lag = now - lastLagCheckTime;
      lag = Math.max(0, lag - lagCheckInterval);

      cb(lag);

      lastLagCheckTime = now;
    }, lagCheckInterval);
  },

  /**
   * Sets property by 'full' dotted path.
   *
   * @param {Object} obj Object for setting.
   * @param {String} path String path with separators.
   * @param {*} value Value of property.
   * @param {String} [separator] Path separator. Dot by default.
   * @returns {Object} Object obj.
   */
  setDeepProperty: function(obj, path, value, separator) {
    let i;

    separator = separator || '.';
    path = path.split(separator);
    let curObj = obj;

    for (i = 0; i < path.length - 1; i++) {
      curObj[path[i]] = curObj[path[i]] || {};
      curObj = curObj[path[i]];
    }

    curObj[path[path.length - 1]] = value;

    return obj;
  },

  /**
   * We have to call ngettext with explicit strings for proper .po generation.
   *
   * @param {Number} n Unit amount.
   * @param {String} timeUnit Time unit string ('minutes', 'hours', 'weeks', 'days', etc.).
   * @returns {String} Proper time unit representation.
   */
  timeUnitsGenitiveTranslations: function(n, timeUnit) {
    return {
      seconds: gettext.ngettext('second_genitive', 'seconds_genitive', n),
      minutes: gettext.ngettext('minute_genitive', 'minutes_genitive', n),
      hours: gettext.ngettext('hour_genitive', 'hours_genitive', n),
      days: gettext.ngettext('day_genitive', 'days_genitive', n),
      weeks: gettext.ngettext('week_genitive', 'weeks_genitive', n),
      months: gettext.ngettext('month_genitive', 'months_genitive', n),
      years: gettext.ngettext('year_genitive', 'years_genitive', n)
    }[timeUnit];
  },

  /**
   * Transforms a given raw value into a human-readable form.
   *
   * @param {any} value Value to transform.
   * @param {Object} meta Field metadata.
   * @param {Object} [options] Additional options.
   * - {String} separator Separator label. Default ' '.
   * - {Boolean} pair Return result as array of number and unit.
   * @returns {String|Number|Array} Human readable value as single value or an array of [value, unit].
   */
  humanReadableValue: function(value, meta, options) {
    let result = '';
    let separator = options && options.separator || ' ';
    let unit = '';
    let pair = options && options.pair || false;
    let res = '';
    let hres = '';
    meta = meta || { };

    switch (meta.type) {
      case 'TIMESTAMP':
        result = UberCommon.localizedDateTime(value);
        separator = ''; // Have no separator for this type.
        break;
      case 'PERCENTILE':
        result = value && value.toFixed(2);
        separator = ''; // Have no separator for this type.
        unit = '%';
        break;
      case 'DURATION':
        res = UberCommon.bringToUnit(value, { strict: false });
        unit = this.timeUnitsGenitiveTranslations(res[0], res[1]);
        result = res[0];
        break;

      default:
        hres = UberCommon.humanReadableNumber(value, {
          bytes: meta.type === 'BYTE_QUANTITY',
          separator,
          pair
        });

        pair ? [result, unit] = hres : result = hres;
    }

    return pair ? [result, unit] : result + (unit ? separator + unit : '');
  },

  /**
   * Attempts to synchronize local time with server.
   *
   * @returns {jQuery.Deferred} Operation promise.
   */
  syncServerTime: function() {
    // Counts number of requests after which we give up
    // if response haven't been received during timeout.
    let requestCounter = 0;
    let syncDfd = new $.Deferred();

    let syncRequest = () => {
      let before = _.now(); // Timestamp before request.

      $.ajax({
        type: 'GET',
        url: '/node/api/time',
        timeout: 3000,
        success: res => {
          let after = _.now(); // Timestamp after response.

          // Calculates delta between client and server time.
          serverTimeDelta = Math.round(res - (after - before) / 2 - before);

          syncDfd.resolve(this.serverTime());
        },
        error: err => {
          if (err.statusText === 'timeout' && requestCounter++ < 5) {
            setTimeout(syncRequest(), 1000);
          }
          else {
            syncDfd.reject('Sync failed: maximum number of attempts reached.');
          }
        }
      });
    };

    syncRequest();

    return syncDfd.promise();
  },

  /**
   * Returns current server timestamp if available, or local timestamp if not.
   *
   * @returns {Number} Current timestamp.
   */
  serverTime: function() {
    return _.now() + serverTimeDelta;
  },

  /**
   * Long version of native setTimeout function, allowing to use timeout with value more than 2147483647.
   *
   * @param {Function} cb Function be called after delay.
   * @param {Number} timeout Number of millisecond to be delayed before cb would be fired.
   * @returns {Object} Timeout object, that can be used to cancel timeout with clearLongTimeout().
   */
  setLongTimeout: function(cb, timeout) {
    if (typeof cb !== 'function') throw new Error('Callback must be a function');

    let timeout0 = parseInt(timeout);

    if (_.isNaN(timeout0)) throw new Error('Timeout must be a number');

    let longTimeout = {
      timer: null,
      clear: function() {
        if (this.timer)
          clearTimeout(this.timer);
      }
    };

    // Setting timeout above this value in native function would fire callback function immediately.
    let maxTimeout = 2147483647;

    if (timeout0 <= maxTimeout) {
      longTimeout.timer = setTimeout(cb, timeout0);
    }
    else {
      let count = Math.floor(timeout0 / maxTimeout); // The number of times we need to delay by max.
      let rem = timeout % maxTimeout; // The length of the final delay.

      (function delay() {
        if (count > 0) {
          count--;
          longTimeout.timer = setTimeout(delay, maxTimeout);
        }
        else {
          longTimeout.timer = setTimeout(cb, rem);
        }
      })();
    }

    return longTimeout;
  },

  /**
   * Clears timeout timer set by setLongTimeout().
   *
   * @param {Object} longTimeoutObject Timeout object, returned by setLongTimeout().
   */
  clearLongTimeout: function(longTimeoutObject) {
    if (!longTimeoutObject || typeof longTimeoutObject.clear !== 'function') throw new Error('Invalid argument');

    longTimeoutObject.clear();
  },

  /**
   * Wraps a given string to hyperlink element (<a>) if it's a URL.
   *
   * @param {String} str String to wrap.
   * @param {String} [options] Operation options.
   * - {Boolean} separateTab If true, will generate external link that opens in separate tab.
   * @returns {String} Hyperlink HTML string or original string, if input string is not a URL.
   */
  urlify: function(str, options) {
    options = options || {};

    if (str.match(/^https?:\/\//)) {
      return '<a href="' + str + '" ' + (options.separateTab ? 'target="_blank"' : '') + '>' +
        '<span>' + str + '</span>' +
        '</a>';
    }

    return str;
  },

  /**
   * Renders gear icon, that indicates awaiting for results.
   *
   * @param {Object} $container Container jQuery element to display gear in.
   * @param {Promise} promise Promise to wait for.
   */
  toggleGear: function($container, promise) {
    let $i = $container.find('i');
    let $origIcon = $i.replaceWith($('<i class="fa fa-gear fa-spin"></i>'));

    // Event handler that restores an original icon.
    promise.always(() => {
      $container.find('i').replaceWith($origIcon);
    });
  },

  /**
   * Generates custom button colors.
   *
   * @param {String} id Css id of button wrapper.
   * @param {String} bgColor Button color in hex format, like #FF0000.
   * @param {String} textColor Text color in hex format.
   * @returns {String} Css styles for style tag.
   */
  generateBtnCss: function(id, bgColor, textColor) {
    textColor = textColor || '#FFF';

    let darken = percent => '#' + tinycolor(bgColor).darken(percent).toHex();

    let borderColor = darken(5);

    return ` 
      #${id} .btn {
        color: ${textColor};
        background-color: ${bgColor};
        border-color: ${borderColor};
      }
      #${id} .btn:hover,
      #${id} .btn:focus,
      #${id} .btn:active,
      #${id} .btn.active,
      #${id} .open .dropdown-toggle.btn {
        color: ${textColor};
        background-color: ${darken(8)};
        border-color: ${darken(12)};
      }
      #${id} .btn:active,
      #${id} .btn.active,
      #${id} .open .dropdown-toggle.btn {
          background-image: none;
      }
      #${id} .btn.disabled,
      #${id} .btn[disabled],
      #${id} fieldset[disabled] .btn
      #${id} fieldset[disabled] .btn,
      #${id} fieldset[disabled] .btn:hover,
      #${id} fieldset[disabled] .btn:focus,
      #${id} fieldset[disabled] .btn:active,
      #${id} fieldset[disabled] .btn.active {
        background-color: ${bgColor};
        border-color: ${borderColor};
      }
      `;
  },

  /**
   * Adds CSS rules to given style sheet.
   *
   * @param {CSSStyleSheet} sheet Document style sheet.
   * @param {String} selector Selector string.
   * @param {String} rules CSS rules string.
   * @param {Number} index Position for rules to be set.
   */
  addCssRule: function(sheet, selector, rules, index) {
    if (sheet.insertRule) {
      sheet.insertRule(selector + '{' + rules + '}', index);
    }
    else if (sheet.addRule) {
      sheet.addRule(selector, rules, index);
    }
  },

  /**
   * Gets default object dimensions on create.
   *
   * @param {Number|String|undefined} classId Saymon class id.
   * @param {Object} clientConfig Client Config for prevent cycling require.
   * @returns {Array} Array of width and height.
   */
  getDefaultObjectDimensions: function(classId, clientConfig) {
    return classId && clientConfig.defaultClassImage[classId] && clientConfig.defaultClassImage[classId].dim ?
      clientConfig.defaultClassImage[classId].dim : [300, 200];
  },

  /**
   * Performs deep object cloning.
   *
   * @param {Object} obj Input object.
   * @returns {Object} Cloned object.
   */
  deepClone: function(obj) {
    if (!_.isObject(obj)) return obj;

    return JSON.parse(JSON.stringify(obj));
  },

  /**
   * Encodes given object as URI parameters.
   *
   * @param {Object} obj Object to encode.
   * @returns {String} URI parameters string with leading '?' or empty string, if input is empty.
   */
  encodeUriParameters: function(obj) {
    if (_.isEmpty(obj)) return '';

    if (!_.isObject(obj)) return encodeURIComponent(obj);

    let params = _.map(obj, (val, key) => {
      let val0 = _.isObject(val) ? JSON.stringify(val) : val;

      return `${key}=${encodeURIComponent(val0)}`;
    });

    return '?' + params.join('&');
  },

  /**
   * Close dropdown on scroll (selectpicker or argument list dropdown).
   *
   * @param {jQuery} $el Element. If element removes we unbind scroll event.
   * @param {String} showEvent Event. In handler of this event we bind scroll handler.
   * @param {Function} scrollHandler Close dropdown element handler.
   */
  closeOnScroll: function($el, showEvent, scrollHandler) {
    $el.one(showEvent, () => {
      document.addEventListener('scroll', scrollHandler, true);
    });

    $el.on('remove', () => {
      scrollHandler && document.removeEventListener('scroll', scrollHandler, true);
    });
  },

  /**
   * Check if we run saymon on mac machine.
   *
   * @returns {Boolean} True if run on mac.
   */
  isMac: function() {
    return navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  },

  /**
   * Checks if ctrl pressed on PC or Command pressed on mac.
   *
   * @param {Object} evt Event.
   * @returns {Boolean} True if pressed.
   */
  isCtrlPressed(evt) {
    let isMac = this.isMac();

    return (isMac && evt.metaKey) || (!isMac && evt.ctrlKey);
  },

  /**
   * Creates entity style from custom style string.
   *
   * @param {String} cssString CSS rules as string.
   * @param {String} entitySelector Entity selector like '#obj2' or '.link3'.
   * @returns {jQuery} JQuery style tag object.
   */
  createEntityStyle: function(cssString, entitySelector) {
    let parser = new cssjs.CssJs();
    // Parse CSS string.
    let parsed = parser.parseCSS(cssString);

    _.each(parsed, function(rule) {
      if (!rule.type) {
        let matches = rule.selector.match(/([^&]*)&([^&]*)/);

        if (matches) {
          rule.selector = matches[1] + entitySelector + matches[2];
        }
        else {
          rule.selector = entitySelector + ' ' + rule.selector;
        }
      }
    });

    return $('<style>').text(parser.getCSSForEditor(parsed));
  },

  /**
   * Converts downsample from numeric to string.
   *
   * @param {Number} downsample Downsample in milliseconds.
   * @returns {String} String with number and suffix.
   */
  convertDownsample: function(downsample) {
    if (!downsample || downsample == 'none') return downsample;

    return `${Math.ceil(downsample / 60000)}m-avg`;
  },

  /**
   * Automatically creates an OpenTSDB downsample parameter for a given time interval.
   *
   * @param {Number} from Timestamp of period begin.
   * @param {Number} to Timestamp of period end.
   * @returns {String} Downsample parameter value.
   */
  generateDownsample: function(from, to) {
    let now = this.serverTime();
    let delta = (to || now) - (from || now);
    let extension = 10000;

    let item = clientConfig.history.downsampling.find(item => {
      if (item.length == 1) {
        return true; // Assume downsampling value is specified immediately.
      }
      else if (item.length == 2) {
        let first = item[0];

        if (_.isNumber(first)) { // Numeric downsampling period.
          return delta <= first + extension;
        }
        else { // Period specified with period-unit pair.
          let periodPair = first;
          let period = UberCommon.millisecondTime(periodPair[0], periodPair[1]);

          return delta <= period + extension;
        }
      }

      return false;
    });

    if (!item) return '';

    if (item.length == 1) return item[0];

    return item[1];
  },

  /**
   * Creates an OpenTSDB downsample parameter from the given value and time interval.
   *
   * @param {Number|String} downsample Downsample in milliseconds or a string with already created OpenTSDB downsample.
   * @param {Number} from Timestamp of period begin.
   * @param {Number} to Timestamp of period end.
   * @returns {String} Downsample parameter value.
   */
  prepareDownsample: function(downsample, from, to) {
    if (downsample === 'none')
      return;

    if (typeof downsample === 'number')
      downsample = this.convertDownsample(downsample);

    return downsample ? downsample : this.generateDownsample(from, to);
  },

  /**
   * Loads and pastes svg image.
   *
   * @param {Object} $container Container for paste.
   * @param {String} url Image url.
   */
  pasteSvg($container, url) {
    $.get(url, {}, $.noop, 'text').done((svgIcon) => {
      $($container).html($(svgIcon).removeAttr('height').removeAttr('width'));
    });
  },

  /**
   * Validates geoposition.
   *
   * @param {Number[]} geoposition Array with longitude and latitude.
   * @returns {Boolean} True if geoposition is valid.
   */
  isValidGeoposition(geoposition) {
    return _.isArray(geoposition) &&
      geoposition.length == 2 &&
      _.all(geoposition, item => _.isNumber(item)) &&
      geoposition[1] >= -90 &&
      geoposition[1] <= 90 &&
      geoposition[0] >= -180 &&
      geoposition[0] <= 180;
  },

  /**
   * Checks if browser is WebKit base.
   * Adds no-webkit class if not base.
   */
  checkWebKit() {
    $('body').toggleClass('no-webkit', !navigator.userAgent.match(/WebKit/i));
  },

  /**
   * Escapes css class name.
   *
   * @see https://github.com/jquery/jquery/blob/
   *   e743cbd28553267f955f71ea7248377915613fd9/external/sizzle/dist/sizzle.js#L1037
   * @param {String} className Class name.
   * @returns {String} Escaped class name.
   */
  escapeCSSClass(className) {
    return className.replace(
      /* eslint-disable no-control-regex */
      /([\0-\x1f\x7f]|^-?\d)|^-$|[^\0-\x1f\x7f-\uFFFF\w-]/g,
      /* eslint-enable no-control-regex */
      (ch, asCodePoint) => {
        if (asCodePoint) {
          // U+0000 NULL becomes U+FFFD REPLACEMENT CHARACTER
          if (ch === '\0') {
            return '\uFFFD';
          }

          // Control characters and (dependent upon position) numbers get escaped as code points
          return ch.slice(0, -1) + '\\' + ch.charCodeAt(ch.length - 1).toString(16) + ' ';
        }

        // Other potentially-special ASCII characters get backslash-escaped
        return '\\' + ch;
      }
    );
  },

  /**
   * Gets entity model string representation for single metric widget.
   * Try to find it in models array.
   * If no result use first model.
   *
   * @param {String[]} models Entity strings like objects-57b5b1dc110a8d232579229d.
   * @param {Object} entity Saymon entity.
   * @returns {Object} Found entity or first entity as string.
   */
  getOneModel(models = [], entity) {
    let model = `${entity.entityType}-${entity.get('id')}`;

    let targetModel = models.find((model0) => model0 == model) || models[0];

    return targetModel ? [targetModel] : [];
  }
};

/**
 * Animates jQuery element by cssClass.
 *
 * @param {String} cssClass Class name.
 * @param {String[]} properties Property names.
 * @param {Object} [options] Animate options.
 * @returns {jQuery} Self object for chaining.
 */
$.fn.animateByClass = function(cssClass, properties, options) {
  let propValues = exports.getCssProperty(cssClass, properties);

  // Convert hyphen-separated CSS property names to camel case names, as required by
  // jQuery.animate().
  let forAnimate = {};
  _.each(properties, function(prop, index) {
    let propCamel = exports.hyphenToCamel(prop);
    forAnimate[propCamel] = propValues[index];
  });

  this.animate(forAnimate, _.extend({ duration: 1000 }, options));

  return this;
};

/**
 * Used to toggle selected="selected", disabled="disabled", checked="checked" etc.
 *
 * @param {String} attr Attribute name.
 * @param {Boolean} [turnOn] Explicitly specify on or off attribute.
 * @param {String} [value] Attribute value. If not set we use attribute name.
 * @returns {jQuery} Self object for chaining.
 */
$.fn.toggleAttr = function(attr, turnOn, value) {
  let justToggle = (turnOn === undefined);

  return this.each(function() {
    if ((justToggle && !$(this).is(`[${attr}]`)) ||
      (!justToggle && turnOn)) {
      $(this).attr(attr, value || attr);
    }
    else {
      $(this).removeAttr(attr);
    }
  });
};

// Include all stuff from UberCommon.
_.extend(exports, UberCommon);

export default exports;

